/**
 * botanico-search.js
 * Soluzione A — doppia barra su /pages/botanico
 *
 * Barra 1: Shopify /search/suggest.json  → risultati inline, zero redirect
 * Barra 2: Backend Vercel /api/botanico  → risposta AI + prodotti
 *
 * Nessuna dipendenza esterna. Va incluso in botanico-widget.liquid
 * tramite {{ 'botanico-search.js' | asset_url | script_tag }}
 * oppure incollato direttamente nel tag <script> dello snippet.
 */

(function () {
  "use strict";

  // ─── Configurazione ────────────────────────────────────────────────────────
  const CONFIG = {
    // Barra 1 — Shopify predictive
    suggestUrl:        "/search/suggest.json",
    suggestTypes:      "product",          // product | article | page | collection
    suggestLimit:      6,
    debounceMs:        280,

    // Barra 2 — Botanico AI
    botanicoEndpoint:  window.__BOTANICO_ENDPOINT__ || "/api/botanico",

    // Selettori DOM (devono corrispondere a botanico-widget.liquid)
    selectors: {
      // Barra 1
      shopifyInput:    "#bw-shopify-input",
      shopifyResults:  "#bw-shopify-results",
      shopifyForm:     "#bw-shopify-form",
      shopifyClear:    "#bw-shopify-clear",

      // Barra 2
      botanicoInput:   "#bw-botanico-input",
      botanicoBtn:     "#bw-botanico-btn",
      botanicoAnswer:  "#bw-botanico-answer",
      botanicoProducts:"#bw-botanico-products",
      botanicoFallback:"#bw-botanico-fallback",
      botanicoLoading: "#bw-botanico-loading",
      botanicoResponse:"#bw-botanico-response",
    },
  };

  // ─── Utility ───────────────────────────────────────────────────────────────

  const $ = (sel) => document.querySelector(sel);

  const debounce = (fn, ms) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), ms);
    };
  };

  const formatMoney = (cents) => {
    return new Intl.NumberFormat("it-IT", {
      style:    "currency",
      currency: "EUR",
    }).format(cents / 100);
  };

  const escapeHtml = (str) =>
    String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");

  // ─── BARRA 1: Shopify Predictive Search ────────────────────────────────────

  const ShopifySearch = (() => {
    const s       = CONFIG.selectors;
    let input     = null;
    let resultsEl = null;
    let formEl    = null;
    let clearBtn  = null;
    let abortCtrl = null;

    // Render di un singolo prodotto nel dropdown
    const renderProduct = (product) => {
      const image = product.image
        ? `<img src="${escapeHtml(product.image.url)}" alt="${escapeHtml(product.title)}" width="44" height="44" loading="lazy">`
        : `<div class="bw-sr-img-placeholder" aria-hidden="true"></div>`;

      const price = product.variants?.[0]?.price
        ? `<span class="bw-sr-price">${formatMoney(product.variants[0].price * 100)}</span>`
        : "";

      return `
        <a href="${escapeHtml(product.url)}" class="bw-sr-item" role="option">
          <div class="bw-sr-img">${image}</div>
          <div class="bw-sr-info">
            <span class="bw-sr-title">${escapeHtml(product.title)}</span>
            ${price}
          </div>
        </a>`;
    };

    // Render del dropdown completo
    const renderResults = (products) => {
      if (!resultsEl) return;

      if (!products || products.length === 0) {
        resultsEl.innerHTML = `<p class="bw-sr-empty">Nessun prodotto trovato.</p>`;
        resultsEl.hidden = false;
        return;
      }

      resultsEl.innerHTML = products.map(renderProduct).join("");
      resultsEl.hidden = false;
      resultsEl.setAttribute("aria-expanded", "true");
    };

    const hideResults = () => {
      if (!resultsEl) return;
      resultsEl.hidden = true;
      resultsEl.innerHTML = "";
      resultsEl.setAttribute("aria-expanded", "false");
    };

    // Chiamata a /search/suggest.json
    const fetchSuggestions = async (query) => {
      if (abortCtrl) abortCtrl.abort();
      abortCtrl = new AbortController();

      const params = new URLSearchParams({
        q:                        query,
        "resources[type]":        CONFIG.suggestTypes,
        "resources[limit]":       CONFIG.suggestLimit,
        "resources[options][unavailable_products]": "last",
        "resources[options][fields]": "title,product_type,variants.title,vendor",
      });

      try {
        const res = await fetch(`${CONFIG.suggestUrl}?${params}`, {
          signal: abortCtrl.signal,
          headers: { "Accept": "application/json" },
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();
        const products = data?.resources?.results?.products ?? [];
        renderResults(products);

      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("[botanico-search] Predictive search error:", err);
        hideResults();
      }
    };

    const debouncedFetch = debounce(fetchSuggestions, CONFIG.debounceMs);

    // Keyboard navigation nel dropdown
    const handleKeydown = (e) => {
      if (!resultsEl || resultsEl.hidden) return;

      const items = Array.from(resultsEl.querySelectorAll(".bw-sr-item"));
      const current = resultsEl.querySelector(".bw-sr-item--focused");
      const idx = items.indexOf(current);

      if (e.key === "ArrowDown") {
        e.preventDefault();
        const next = items[idx + 1] ?? items[0];
        current?.classList.remove("bw-sr-item--focused");
        next?.classList.add("bw-sr-item--focused");
        next?.focus();
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        const prev = items[idx - 1] ?? items[items.length - 1];
        current?.classList.remove("bw-sr-item--focused");
        prev?.classList.add("bw-sr-item--focused");
        prev?.focus();
      } else if (e.key === "Escape") {
        hideResults();
        input?.focus();
      }
    };

    const init = () => {
      input     = $(s.shopifyInput);
      resultsEl = $(s.shopifyResults);
      formEl    = $(s.shopifyForm);
      clearBtn  = $(s.shopifyClear);

      if (!input || !resultsEl || !formEl) return;

      // Blocca il redirect — risultati già inline
      formEl.addEventListener("submit", (e) => {
        e.preventDefault();
        const q = input.value.trim();
        if (q) fetchSuggestions(q);
      });

      // Typing → suggerimenti live
      input.addEventListener("input", () => {
        const q = input.value.trim();
        clearBtn && (clearBtn.hidden = q.length === 0);
        if (q.length >= 2) {
          debouncedFetch(q);
        } else {
          hideResults();
        }
      });

      // Tastiera
      input.addEventListener("keydown", handleKeydown);
      resultsEl.addEventListener("keydown", handleKeydown);

      // Chiudi cliccando fuori
      document.addEventListener("click", (e) => {
        if (!formEl.contains(e.target)) hideResults();
      });

      // Clear button
      clearBtn?.addEventListener("click", () => {
        input.value = "";
        clearBtn.hidden = true;
        hideResults();
        input.focus();
      });
    };

    return { init };
  })();

  // ─── BARRA 2: Botanico AI ──────────────────────────────────────────────────

  const BotanicoAI = (() => {
    const s = CONFIG.selectors;

    let input     = null;
    let btn       = null;
    let answer    = null;
    let products  = null;
    let fallback  = null;
    let loading   = null;
    let response  = null;

    const setLoading = (show) => {
      loading.hidden  = !show;
      btn.disabled    = show;
      btn.textContent = show ? "Cerco…" : "Cerca";
    };

    // Render prodotti consigliati dall'AI
    const renderProducts = (items) => {
      if (!items || items.length === 0) {
        fallback.hidden = false;
        products.innerHTML = "";
        return;
      }
      fallback.hidden = true;
      products.innerHTML = items.map((p) => `
        <a href="${escapeHtml(p.url)}" class="bw-product-card">
          ${p.image
            ? `<img src="${escapeHtml(p.image)}" alt="${escapeHtml(p.title)}" loading="lazy">`
            : `<div class="bw-product-card__placeholder" aria-hidden="true"></div>`
          }
          <div class="bw-product-card__info">
            <p class="bw-product-card__title">${escapeHtml(p.title)}</p>
            ${p.price ? `<p class="bw-product-card__price">${escapeHtml(p.price)}</p>` : ""}
          </div>
        </a>
      `).join("");
    };

    const run = async () => {
      const query = input.value.trim();
      if (!query) {
        input.focus();
        return;
      }

      setLoading(true);
      response.hidden  = true;
      answer.textContent = "";
      products.innerHTML = "";
      fallback.hidden  = true;

      try {
        const res = await fetch(CONFIG.botanicoEndpoint, {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify({ query }),
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();

        answer.textContent = data.answer ?? "";
        renderProducts(data.products ?? []);
        response.hidden = false;

      } catch (err) {
        console.error("[botanico-search] Botanico AI error:", err);
        answer.textContent =
          "Qualcosa non ha funzionato. Riprova o scrivici su WhatsApp.";
        fallback.hidden  = false;
        response.hidden  = false;
      } finally {
        setLoading(false);
      }
    };

    const init = () => {
      input    = $(s.botanicoInput);
      btn      = $(s.botanicoBtn);
      answer   = $(s.botanicoAnswer);
      products = $(s.botanicoProducts);
      fallback = $(s.botanicoFallback);
      loading  = $(s.botanicoLoading);
      response = $(s.botanicoResponse);

      if (!input || !btn) return;

      btn.addEventListener("click", run);
      input.addEventListener("keydown", (e) => {
        if (e.key === "Enter") run();
      });
    };

    return { init };
  })();

  // ─── Bootstrap ─────────────────────────────────────────────────────────────

  const init = () => {
    ShopifySearch.init();
    BotanicoAI.init();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

})();
